import { ensureAnonymousAuth } from '../firebase';
import { useRoleStore } from '../stores/roleStore';
import useUserStore from '../stores/userStore';
import { hasGoogleProvider } from '../utils/authIdentity';
import { observeAuthState } from './authProviders';
import { fetchUserSessions, recordSessionMembership } from './sessionHistory';
import { upsertUserProfile } from './userProfile';
import { isEventLive } from '../utils/eventStatus';

/**
 * Bootstrap identity for any visitor.
 *
 * Anonymous auth remains the default so a guest can join with no sign-in. A
 * persisted Google user is restored by `ensureAnonymousAuth` without creating
 * an extra anonymous account.
 */
export async function initializeAuthSession() {
  const user = await ensureAnonymousAuth();
  useRoleStore.getState().setAuthenticatedUser(user.uid);
  useUserStore.getState().setUser(user);

  // Profiles are written for every uid, not only Google ones, so a later
  // anonymous -> Google link upgrades an existing document in place instead of
  // starting a new identity.
  upsertUserProfile(user).then((profile) => {
    if (profile) useUserStore.getState().setProfile(profile);
  });

  return user;
}

/**
 * Keep the user store aligned with Firebase Auth for the life of the tab.
 * Returns the unsubscribe function.
 */
export function watchAuthState() {
  return observeAuthState((user) => {
    const store = useUserStore.getState();

    if (!user) {
      store.clearUser();
      useRoleStore.getState().setAuthenticatedUser(null);
      return;
    }

    store.setUser(user);
    useRoleStore.getState().setAuthenticatedUser(user.uid);
  });
}

/** Refresh the "My Sessions" index for the current uid. */
export async function loadSessionHistory(uid) {
  if (!uid) {
    useUserStore.getState().setSessions([]);
    return [];
  }

  useUserStore.getState().setSessionsLoading(true);
  const sessions = await fetchUserSessions(uid);
  useUserStore.getState().setSessions(sessions);
  return sessions;
}

/**
 * Called after a successful create or join so the event shows up under
 * "My Sessions". Never blocks entering the party.
 */
export async function rememberSessionMembership(eventData, role) {
  const userId = useRoleStore.getState().userId;
  if (!userId || !eventData?.id) return null;

  return recordSessionMembership({
    uid: userId,
    eventId: eventData.id,
    title: eventData.title || '',
    role: role === 'host' ? 'host' : 'guest',
    createdAt: eventData.createdAt ?? null,
    endedAt: eventData.endedAt ?? null,
    status: eventData.status ?? null,
    clearLeave: isEventLive(eventData),
  });
}

/**
 * Refresh the profile document after a Google link or sign-in so
 * `accountType`, `displayName` and `photoURL` reflect the provider.
 *
 * Also writes the Firebase user into userStore immediately. `linkWithPopup`
 * keeps the same uid, so `onAuthStateChanged` often does not fire and the
 * home screen would otherwise stay on the anonymous snapshot until a reload.
 */
export async function syncProfileAfterAuthChange(user, { displayName = null } = {}) {
  if (!user?.uid) return null;

  useUserStore.getState().setUser(user);
  useRoleStore.getState().setAuthenticatedUser(user.uid);

  const profile = await upsertUserProfile(user, { displayName });
  if (profile) useUserStore.getState().setProfile(profile);
  if (hasGoogleProvider(user)) {
    loadSessionHistory(user.uid).catch(() => {});
  }
  return profile;
}
